import { ReactElement, useEffect, useState } from "react";
import Questionnaire from "./Questionnaire";
import {
  defaultAnswerValueByType,
  formatAnswerForPost,
  formatAnswers,
  getAnswerValue,
  hasAsweredFunctionsByType,
} from "../../../helper/answer";
import { restoreInstanceAnswers } from "../../../helper/questionnaire";
import {
  getApiQuestionnaire,
  getDependencies,
  postAnswers,
} from "../../../services/questionnaire.service";
import ResonanceStore from "../../../storage/resonanceStorage";
import {
  QuestionnaireDetailsType,
  QuestionnaireSession,
} from "../../../types/Questionnaire";
import ResonanceLoader from "../../atoms/ResonanceLoader/ResonanceLoader";
import { useConfig } from "../../../contexts/ConfigContext";
import { QuestionType, QuestionTypeEnum } from "../../../types/Question";
import {
  AnswerType,
  PostAnswersBodyType,
  PostAnswersParamsType,
} from "../../../types/Answer";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Info from "../../atoms/ErrorComponent";
import { getSequenceAnswers } from "../../../services/sequence.service";
import { useAuth } from "../../../contexts/AuthContext";
import useToast from "../../../hooks/useToast";
import { Dependency } from "../../../types/Dependency";
import { useResonanceData } from "../ResonanceContext/ResonanceDataContext";

const generateNewSession = async (
  studyId: number,
  sequenceId: number,
  questionnaireId: number,
  userId: number,
  lang: string,
): Promise<QuestionnaireSession | void> => {
  const { data } = await getApiQuestionnaire(studyId, questionnaireId, lang);
  const {
    data: { answers: savedAnswers },
  } = await getSequenceAnswers({
    study_id: studyId,
    user_id: userId,
    sequence_id: sequenceId,
  });

  const questionners_answers = savedAnswers.filter(
    (answer) => answer.questionnaire_id === questionnaireId,
  );

  const { data: dependencies } = await getDependencies({
    studyId,
    questionnaireId,
  });

  const formatted_answers = formatAnswers(questionners_answers);

  const { questions, version, is_scored } = data;
  if (questions.length === 0) return;

  const answers = [];

  let lastResponse = 0;
  questions.forEach((question, index) => {
    // Top-level (non-instance) answer; set-member instances are restored separately below.
    const ans = formatted_answers.find(
      (a) => a.question_id === question.id && !(Number(a.instance_index) > 0),
    );
    if (ans) lastResponse = index;
    answers.push({
      value: ans
        ? getAnswerValue(question.type, ans)
        : defaultAnswerValueByType[question.type],
      questionId: question.id,
      questionType: question.type,
    });
  });

  // Restore saved set-member instance answers so they aren't blank after a reload.
  answers.push(...restoreInstanceAnswers(questions, formatted_answers));

  // With follow-up sets the flow is dynamic (members are injected as instances),
  // so resume-by-slice doesn't apply — start at the first top-level question.
  const topLevel = questions.filter((q) => q.set_driver_question_id == null);
  const hasSets = topLevel.length !== questions.length;
  let history: QuestionType[] = hasSets
    ? topLevel.slice(0, 1)
    : questions.slice(0, lastResponse + 1);

  return {
    questions,
    answers,
    history,
    version,
    dependencies,
    is_scored,
    sections: data.sections ?? [],
  };
};

const defaultSession: QuestionnaireSession = {
  questions: [],
  answers: [],
  history: [],
  dependencies: [],
  version: 0,
  is_scored: false,
};

interface SequenceQuestionnaireProps {
  questionnaireId: number;
  questionnaireTitle: string;
  onNextClick(): void;
  onPrevClick(): void;

  onQuestionnaireDone?: (totalScore: number | null) => void | Promise<void>;
  // new props (pre-fetched from SequenceComponent)
  questionnaireData?: QuestionnaireDetailsType;
  answers?: any[];
  dependencies?: Dependency[];
  componentId: number;
}

const SequenceQuestionnaire = ({
  questionnaireTitle,
  questionnaireId,
  onNextClick,
  onPrevClick,
  onQuestionnaireDone,
  questionnaireData,
  answers,
  dependencies,
  componentId,
}: SequenceQuestionnaireProps): ReactElement => {
  const [session, setSession] = useState<QuestionnaireSession>(defaultSession);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { sequenceId } = useParams<{ sequenceId: string }>();
  const { user } = useAuth();
  const present = useToast();

  const { updateSequenceComponentProgress } = useResonanceData();
  const { questions } = session;
  const storeKey = `study_${questionnaireId}_session`;
  const { study } = useConfig();
  const {
    t,
    i18n: { language },
  } = useTranslation();

  console.log("[SequenceQuestionnaire] props:", {
    questionnaireId,
    questionnaireTitle,
    questionnaireData,
    answers,
    dependencies,
  });

  useEffect(() => {
    console.log("[SequenceQuestionnaire] mounted once");
  }, []);

  useEffect((): void => {
    const initSession = async (): Promise<void> => {
      try {
        setIsLoading(true);
        setIsError(false);

        let newSession: QuestionnaireSession | void;

        // ✅ If data was passed in from parent, build session directly
        if (questionnaireData && answers && dependencies) {
          const formatted_answers = formatAnswers(answers ?? []);
          const { questions, version } = questionnaireData;

          const sessionAnswers = questions.map((q, index) => {
            const ans = formatted_answers.find(
              (a) => a.question_id === q.id && !(Number(a.instance_index) > 0),
            );
            return {
              value: ans
                ? getAnswerValue(q.type, ans)
                : defaultAnswerValueByType[q.type],
              questionId: q.id,
              questionType: q.type,
            };
          });

          let lastResponse = 0;
          sessionAnswers.forEach((ans, index) => {
            if (
              !hasAsweredFunctionsByType[ans.questionType] ||
              hasAsweredFunctionsByType[ans.questionType](ans.value)
            ) {
              lastResponse = index;
            }
          });

          // Restore saved set-member instance answers (keyed by instanceKey) so they
          // aren't blank after a reload; appended after lastResponse (top-level only).
          sessionAnswers.push(...restoreInstanceAnswers(questions, formatted_answers));

          const topLevel = questions.filter((q) => q.set_driver_question_id == null);
          const hasSets = topLevel.length !== questions.length;
          const history: QuestionType[] = hasSets
            ? topLevel.slice(0, 1)
            : questions.slice(0, lastResponse + 1);

          newSession = {
            is_scored: questionnaireData.is_scored ?? false,
            questions,
            answers: sessionAnswers,
            history,
            version,
            dependencies,
            sections: questionnaireData.sections ?? [],
          };
        } else {
          // fallback to old behavior (still modular)
          newSession = await generateNewSession(
            study.id,
            Number(sequenceId),
            questionnaireId,
            user.id,
            language,
          );
        }

        // await ResonanceStore.set(storeKey, newSession);
        if (newSession) {
          setSession(newSession);
        }
      } catch (error) {
        setIsError(true);
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    initSession();
  }, [
    storeKey,
    questionnaireId,
    language,
    questionnaireData,
    answers,
    dependencies,
    study.id,
    sequenceId,
    user.id,
  ]);

  useEffect((): void => {
    ResonanceStore.set(storeKey, session);
  }, [session, storeKey]);

  const onSubmitAnswer = async (
    question: QuestionType,
    answer: AnswerType,
  ): Promise<void> => {
    try {
      const params: PostAnswersParamsType = {
        studyId: study.id,
        userSequenceId: sequenceId,
        questionnaireId: questionnaireId,
        questionId: question.id,
      };

      const body = formatAnswerForPost(
        question,
        answer.value,
        session.version,
        question.runtime?.instanceIndex ?? 0,
      );
      await postAnswers(params, body);

      const answeredCount = session.answers.filter((a) =>
        hasAsweredFunctionsByType[a.questionType]
          ? hasAsweredFunctionsByType[a.questionType](a.value)
          : false,
      ).length;

      updateSequenceComponentProgress(
        Number(sequenceId),
        componentId,
        answeredCount,
      );
    } catch (e) {
      console.log(e);
      // Identity-question rejections get translated messages (the raw server strings are
      // English-only): 409 = email/phone already used by another account; 422 = external id
      // (numéro de dossier) not in the study's allowlist.
      present(
        e?.response?.status === 409 ? t("questionnaire_identity_conflict")
          : e?.response?.status === 422 ? t("questionnaire_identity_unknown_external_id")
            : e?.response?.data?.message
      );
    }
  };

  if (isLoading) return <ResonanceLoader />;

  if (isError)
    return (
      <Info
        title={t("common_server_error_title")}
        description={t("common_server_error_desc")}
        actionText={t("common_go_back")}
        onClick={onPrevClick}
      />
    );

  if (!questions || questions.length === 0)
    return (
      <Info
        title={t("common_nodata_title")}
        description={t("common_nodata_desc")}
        actionText={t("common_go_back")}
        onClick={onPrevClick}
      />
    );

  return (
    <Questionnaire
      questionnaireTitle={questionnaireTitle}
      session={session}
      onSessionChange={setSession}
      onSubmitAnswer={onSubmitAnswer}
      onNextClick={onNextClick}
      onPrevClick={onPrevClick}
      onQuestionnaireDone={(score) => onQuestionnaireDone?.(score)}
    />
  );
};

export default SequenceQuestionnaire;
